import { useCallback, useMemo } from 'react';

import { usePathname, useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';

import { ErrorCode } from '@repo/contracts/errors';
import { useDisplayRequestErrors as useBaseDisplayRequestErrors } from '@repo/ui/hooks/useDisplayRequestErrors';
import { useNotification } from '@repo/ui/hooks/useNotification';

import { useAppContext } from '@context/AppContext';

import { getErrorConfig } from '@utils/errors';

export interface NliApiError {
    status?: number;
    message: string;
    details?: unknown;
}

export interface NliErrorCallback {
    errorCode: ErrorCode;
    fn?: () => void;
}

type DisplayNliApiErrorFunction = (
    error: NliApiError,
    errorCallbacks?: NliErrorCallback[],
    exclude401?: boolean,
    exclude403?: boolean,
) => void;

export const useAppDisplayRequestErrors = (): DisplayNliApiErrorFunction => {
    const t = useTranslations();
    const notification = useNotification();
    const baseDisplay = useBaseDisplayRequestErrors();
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const currentPath = useMemo(() => `${pathname}${searchParams.toString() ? `?${searchParams.toString()}` : ''}`, [pathname, searchParams.toString()]);
    const [, setErrorReport] = useAppContext('errorReport');

    const display: DisplayNliApiErrorFunction = useCallback((
        error,
        errorCallbacks = [],
        exclude401 = false,
        exclude403 = false,
    ) => {
        const handledByBase = baseDisplay(
            { message: error.message, status: error.status },
            [],
            exclude401,
            exclude403,
            false, // do not show fallback error
        );
        if (handledByBase) return;

        // Exécuter les callbacks correspondants (actions supplémentaires)
        for (const errorCallback of errorCallbacks) {
            if (error.message === errorCallback.errorCode) {
                errorCallback.fn?.();
                break;
            }
        }

        let dictionaryConfig = getErrorConfig(error.message);

        if (!exclude403 && error.status === 403) {
            // Afficher la notification basée sur le type d'erreur 403
            if (error.message === 'FORBIDDEN') {
                notification('warning', t('you-do-not-have-the-required-rights'));
                return;
            } else if (error.message === 'BOOND_API_ERROR') {
                notification('warning', t('access-to-certain-data-in-boond-is-limited-for-optimal-use'));
                return;
            } else {
                dictionaryConfig = getErrorConfig('FORBIDDEN_403');
            }
        }


        // Afficher la notification basée sur le dictionnaire
        if (dictionaryConfig) {
            const resolvedMessage =
                    dictionaryConfig.messageKey ? t(dictionaryConfig.messageKey) : undefined;
            if (resolvedMessage) {
                notification(dictionaryConfig.severity, resolvedMessage, dictionaryConfig.closeDelay);
                return;
            }
            console.error(`Error code ${error.message} found in dictionary but messageKey is missing or invalid.`);
        }

        console.error('Request error', {
            message: error.message,
            status: error.status,
            details: error.details,
            error,
        });
        notification('error', t('an-error-occurred'));

        const syntheticError = Object.assign(new Error(error.message), error);
        // Object.assign ne copie pas `stack` (propriété non-énumérable sur les Error natifs).
        // On la copie explicitement pour conserver la stack originale de l'erreur.
        if (error instanceof Error && error.stack) {
            syntheticError.stack = error.stack;
        } else if (!syntheticError.stack) {
            // Pas de stack originale : on en crée une au point d'appel
            syntheticError.stack = new Error().stack ?? '';
        }

        // Ne pas rouvrir la modal si cette erreur précise a été ignorée pour la session
        if (typeof window !== 'undefined') {
            try {
                const raw = localStorage.getItem('nli:errorReport:ignored');
                if (raw) {
                    const firstFrame = syntheticError.stack
                        ?.split('\n')
                        .map((l) => l.trim())
                        .find((l) => l.startsWith('at ') || l.includes('@'));
                    const fingerprint = `${syntheticError.message}|${firstFrame ?? ''}`;
                    const ignored: string[] = JSON.parse(raw);
                    if (ignored.includes(fingerprint)) return;
                }
            } catch {
                // localStorage indisponible → silencieux
            }
        }

        setErrorReport({
            show: true,
            error: syntheticError,
            url: currentPath,
        });
    }, [currentPath]);

    return display;
};
